import React from "react";
import {Sidebar} from "../../components/Sidebar";
import styled from "styled-components";
import {ArrowLeftIcon, XMarkIcon} from "@heroicons/react/24/outline";
import {useNavigate, useParams} from "react-router-dom";
import axios from "axios";
import {useQuery} from "react-query";
import AppLayout from "../../layouts/AdminLayout";
import html2pdf from "html2pdf.js";

interface ManifestData {
  message: string;
  data: {
    riderInfo: {
      fullName: string;
      phoneNumber: string;
      rideType: string;
    };
    items: {
      recipientAddress: string;
      recipientphoneNumber: string;
      recipientName: string;
      itemWeight: string;
      itemDescription: string;
      waybillNumber: string;
      senderAddress: string;
      senderphoneNumber: string;
      senderName: string;
      orderId: string;
    }[];
    id: string;
    fleetId: string;
    generatedNumber: string;
    createdAt: string;
    updatedAt: string;
  };
}

export const ManifestPreview = () => {
  const {manifestID} = useParams<{manifestID: string}>();
  const Baseurl = process.env.REACT_APP_BASEURL;
  const navigate = useNavigate();

  const token = JSON.parse(localStorage.getItem("eftd-admin-token") || "{}")
    ?.data?.data?.token;
  const config = {
    headers: {Authorization: `Bearer ${token}`},
  };

  const fetchManifest = async () => {
    const response = await axios.get(
      `${Baseurl}/api/admin/orders/manifests/${manifestID}`,
      config
    );
    return response?.data;
  };

  const {
    data: manifest,
    isLoading,
    refetch,
  } = useQuery<ManifestData>(["ORDER", manifestID], fetchManifest, {
    enabled: !!manifestID,
  });

  const handleDownloadPDF = () => {
    const element = document.getElementById("receipt-pdf");
    if (!element || !manifest) return;

    const clonedElement = element.cloneNode(true) as HTMLElement;
    document.body.appendChild(clonedElement);

    const opt = {
      margin: 0,
      filename: `Order receipt for - ${manifest.data.id}.pdf`,
      html2canvas: {
        scale: 2,
        logging: true,
        useCORS: true,
        allowTaint: true,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      },
    };

    html2pdf().from(clonedElement).set(opt).save();
  };

  const handlePrintPDF = () => {
    const element = document.getElementById("receipt-pdf");
    if (!element || !manifest) return;

    const clonedElement = element.cloneNode(true) as HTMLElement;
    document.body.appendChild(clonedElement);

    const opt = {
      margin: 0,
      filename: `Order receipt for - ${manifest.data.id}.pdf`,
      html2canvas: {
        scale: 2,
        logging: true,
        useCORS: true,
        allowTaint: true,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      },
    };

    html2pdf()
      .from(clonedElement)
      .set(opt)
      .outputPdf("dataurlnewwindow")
      .finally(() => {
        document.body.removeChild(clonedElement);
      });
  };

  if (isLoading) {
    return <AppLayout>Loading...</AppLayout>;
  }

  if (!manifest) {
    return <AppLayout>No manifest data found</AppLayout>;
  }

  const createdAt = new Date(manifest.data.createdAt).toLocaleDateString(
    "en-US",
    {
      year: "numeric",
      month: "long",
      day: "numeric",
    }
  );

  return (
    <AppLayout>
      <div className="!flex !items-center !justify-between !p-4">
        <button
          className="!flex !items-center !gap-2 !mb-6"
          onClick={() => navigate("/deliveries")}
        >
          <ArrowLeftIcon width={24} height={24} color="#666D80" />
          <p className="!text-sm !text-[#666d80]">Go Back</p>
        </button>
        {/* <button>
          <XMarkIcon width={24} height={24} color="#666D80" />
        </button> */}
      </div>
      <div className="!flex !justify-center !gap-4 !p-4">
        <button
          className="!bg-white !text-[#FF7A00] !border !border-[#FF7A00] !rounded-md !px-6 !py-3 !text-sm !font-semibold"
          onClick={handleDownloadPDF}
        >
          Download Manifest
        </button>
        <button
          className="!bg-[#FF7A00] !text-white !rounded-md !px-6 !py-3 !text-sm !font-semibold"
          onClick={handlePrintPDF}
        >
          Print Manifest
        </button>
      </div>
      <div
        id="receipt-pdf"
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0px 10px 20px 0px #25314C26",
          paddingLeft: "40px",
          paddingRight: "40px",
          paddingTop: "28px",
          paddingBottom: "28px",
          marginLeft: "auto",
          marginRight: "auto",
          maxWidth: "672px",
          marginTop: "16px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "16px",
            gap: "24px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              width: "50%",
              height: "100px",
              border: "1px solid #31EAF4",
              borderRadius: "8px",
            }}
          >
            <img
              src="/logo.png"
              alt="eFTD Africa Logo"
              style={{height: "32px", marginRight: "8px"}}
            />
            <h2 style={{fontSize: "20px", fontWeight: 600, color: "#333"}}>
              eFTD Africa
            </h2>
          </div>
          <div
            style={{
              width: "50%",
              boxShadow: "0px 2px 19px 0px #305F9712",
              padding: "16px",
              border: "1px solid #EBEFF6",
              borderRadius: "8px",
              display: "flex",
            }}
          >
            <p
              style={{
                fontSize: "10px",
                color: "#5D6481",
                display: "flex",
                flexDirection: "column",
                paddingRight: "16px",
                borderRight: "1px solid #EBEFF6",
                width: "50%",
              }}
            >
              <span>Created On: </span>
              <span
                style={{fontWeight: 600, fontSize: "12px", color: "#19213D"}}
              >
                {createdAt}
              </span>
            </p>
            <p
              style={{
                fontSize: "10px",
                color: "#5D6481",
                paddingLeft: "16px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <span>Manifest Number: </span>
              <span
                style={{fontSize: "12px", fontWeight: 600, color: "#01CCD9"}}
              >
                {manifest.data.generatedNumber}
              </span>
            </p>
          </div>
        </div>

        <hr
          style={{
            marginTop: "24px",
            marginBottom: "24px",
            borderColor: "#EBEFF6",
          }}
        />

        <h3 style={{fontSize: "10px", color: "#19213D", marginBottom: "8px"}}>
          Rider Information
        </h3>
        <div
          style={{
            marginBottom: "16px",
            paddingTop: "8px",
            paddingBottom: "8px",
            paddingLeft: "16px",
            paddingRight: "16px",
            backgroundColor: "#F6F8FC",
            borderRadius: "8px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontSize: "10px",
              color: "#666D80",
            }}
          >
            <p>{manifest.data.riderInfo.fullName}</p>
            <div
              style={{
                marginLeft: "16px",
                marginRight: "16px",
                borderLeft: "1px solid #D1D5DB",
                height: "16px",
              }}
            ></div>
            <p>{manifest.data.riderInfo.phoneNumber}</p>
            <div
              style={{
                marginLeft: "16px",
                marginRight: "16px",
                borderLeft: "1px solid #D1D5DB",
                height: "16px",
              }}
            ></div>
            <p>
              {manifest.data.riderInfo.rideType === "1"
                ? "Motorcycle"
                : "Vehicle"}
            </p>
          </div>
        </div>

        <div style={{marginTop: "16px"}}>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#333",
              marginBottom: "8px",
            }}
          >
            Delivery Information
          </h3>
          {manifest.data.items.map((item, index) => (
            <div
              key={index}
              style={{
                padding: "16px",
                marginBottom: "16px",
                border: "1px solid #E5E7EB",
                borderRadius: "6px",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: "16px",
                  fontSize: "14px",
                  color: "#666D80",
                }}
              >
                <div>
                  <h4
                    style={{
                      fontWeight: 600,
                      color: "#555",
                      marginBottom: "4px",
                    }}
                  >
                    Sender
                  </h4>
                  <p>
                    <span style={{fontWeight: 600}}>Name:</span>{" "}
                    {item.senderName}
                  </p>
                  <p>
                    <span style={{fontWeight: 600}}>Address:</span>{" "}
                    {item.senderAddress}
                  </p>
                  <p>
                    <span style={{fontWeight: 600}}>Phone:</span>{" "}
                    {item.senderphoneNumber}
                  </p>
                </div>
                <div>
                  <h4
                    style={{
                      fontWeight: 600,
                      color: "#555",
                      marginBottom: "4px",
                    }}
                  >
                    Receiver
                  </h4>
                  <p>
                    <span style={{fontWeight: 600}}>Name:</span>{" "}
                    {item.recipientName}
                  </p>
                  <p>
                    <span style={{fontWeight: 600}}>Address:</span>{" "}
                    {item.recipientAddress}
                  </p>
                  <p>
                    <span style={{fontWeight: 600}}>Phone:</span>{" "}
                    {item.recipientphoneNumber}
                  </p>
                  <p>
                    <span style={{fontWeight: 600}}>Waybill No:</span>{" "}
                    {item.waybillNumber || "N/A"}
                  </p>
                </div>
              </div>
              <div
                style={{marginTop: "8px", fontSize: "14px", color: "#666D80"}}
              >
                <p>
                  <span style={{fontWeight: 600}}>Product Weight:</span>{" "}
                  {item.itemWeight}
                </p>
                <p>
                  <span style={{fontWeight: 600}}>Item Description:</span>{" "}
                  {item.itemDescription}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>{" "}
    </AppLayout>
  );
};

const Body = styled.main`
  display: flex;
`;
const Wrapper = styled.main`
  width: 85vw;
  overflow-y: scroll;
  margin-left: 15vw;
  padding: 20px;
  @media (max-width: 425px) {
    width: 100vw;
    margin-left: 0;
  }
`;
